import {
  Stack,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Picture, TImage } from '@shared/ui'

import arbipayPng from '../../assets/arbipay-banking-banner.png'
import arbipayWebp from '../../assets/arbipay-banking-banner.webp'
import cashDepositAndWithdrawalPng from '../../assets/cash-deposit-and-withdrawal.png'
import cashDepositAndWithdrawalWebp from '../../assets/cash-deposit-and-withdrawal.webp'
import openingBankAccountsPng from '../../assets/opening-bank-accounts.png'
import openingBankAccountsWebp from '../../assets/opening-bank-accounts.webp'
import ownNetworkPng from '../../assets/own-network.png'
import ownNetworkWebp from '../../assets/own-network.webp'
import paymentGatewayPng from '../../assets/payment-gateway.png'
import paymentGatewayWebp from '../../assets/payment-gateway.webp'
import physicalVirtualPng from '../../assets/physical-virtual.png'
import physicalVirtualWebp from '../../assets/physical-virtual.webp'
import posTerminalsPng from '../../assets/POS-terminals.png'
import posTerminalsWebp from '../../assets/POS-terminals.webp'

import { ProjectTitle, ProjectName } from '../components'

const BoardElement = styled('div')({
  maxWidth: 400,
  display: 'flex',
})

const Board = styled('div')(({ theme }) => ({
  position: 'relative',
  background:
    'linear-gradient(134.87deg, #E7ECF4 8.95%, #D0D8ED 53.4%, #CDDBFB 90.26%)',
  width: '100%',
  height: '100%',
  display: 'grid',
  gridTemplateColumns: '1fr',
  gridTemplateRows: 'repeat(6, auto)',
  gridAutoFlow: 'row',
  rowGap: theme.spacing(2.5),
  columnGap: theme.spacing(7.5),
  padding: theme.spacing(4, 3),
  minHeight: '420px',
  borderRadius: '20px',
  [theme.breakpoints.up('md')]: {
    rowGap: theme.spacing(4),
    gridTemplateColumns: 'repeat(2, 1fr)',
    gridTemplateRows: 'repeat(3, auto)',
    gridAutoFlow: 'column',
    padding: theme.spacing('40px', '60px'),
  },
}))

export function ArbipayProject() {
  const { t } = useTranslation('widgets')

  const theme = useTheme()

  const isMdAndUp = useMediaQuery(theme.breakpoints.up('md'))

  return (
    <Stack>
      <ProjectTitle number={2} />

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems="flex-end"
        gap={{
          xs: 4,
          md: 7.5,
        }}
      >
        <Stack gap={{ xs: 2.5, md: 5, flex: 1 }}>
          <ProjectName
            sx={{
              mt: { xs: 3, md: 4.5 },
            }}
            name={t('main-page.projects.arbipay.name')}
            description={t('main-page.projects.arbipay.description')}
          />

          <Stack gap={{ xs: 1.5, md: 2.5 }}>
            <Typography
              fontStyle="italic"
              variant={isMdAndUp ? 'text20Italic' : 'text16Regular'}
            >
              {t('main-page.projects.arbipay.text-1')}
            </Typography>

            <Typography
              sx={{ fontWeight: 400 }}
              variant={isMdAndUp ? 'text18Regular' : 'text16Regular'}
            >
              {t('main-page.projects.arbipay.text-2')}
            </Typography>
          </Stack>
        </Stack>

        <Picture
          png={arbipayPng as TImage<'png'>}
          webp={arbipayWebp as TImage<'webp'>}
          alt="Arbipay"
          sx={{
            flex: 1,
            maxHeight: { md: '370px' },
          }}
        />
      </Stack>

      <Board
        sx={{
          margin: { xs: '32px -24px 0', md: '100px 0 0' },
          width: { xs: 'calc(100% + 48px)', md: '100%' },
        }}
      >
        <BoardElement>
          <Stack direction="row" alignItems="center" gap={2.5}>
            <Picture
              png={openingBankAccountsPng as TImage<'png'>}
              webp={openingBankAccountsWebp as TImage<'webp'>}
              alt={t('main-page.projects.arbipay.board.elements.0.title')}
              sx={{ maxWidth: { xs: '64px', md: '72px' } }}
            />

            <Stack>
              <Typography variant="text18Bold">
                {t('main-page.projects.arbipay.board.elements.0.title')}
              </Typography>
            </Stack>
          </Stack>
        </BoardElement>

        <BoardElement>
          <Stack direction="row" alignItems="center" gap={2.5}>
            <Picture
              png={paymentGatewayPng as TImage<'png'>}
              webp={paymentGatewayWebp as TImage<'webp'>}
              alt={t('main-page.projects.arbipay.board.elements.1.title')}
              sx={{ maxWidth: { xs: '64px', md: '72px' } }}
            />

            <Stack>
              <Typography variant="text18Bold">
                {t('main-page.projects.arbipay.board.elements.1.title')}
              </Typography>
            </Stack>
          </Stack>
        </BoardElement>

        <BoardElement>
          <Stack direction="row" alignItems="center" gap={2.5}>
            <Picture
              png={ownNetworkPng as TImage<'png'>}
              webp={ownNetworkWebp as TImage<'webp'>}
              alt={t('main-page.projects.arbipay.board.elements.2.title')}
              sx={{ maxWidth: { xs: '64px', md: '72px' } }}
            />

            <Stack>
              <Typography variant="text18Bold">
                {t('main-page.projects.arbipay.board.elements.2.title')}
              </Typography>
            </Stack>
          </Stack>
        </BoardElement>

        <BoardElement>
          <Stack direction="row" alignItems="center" gap={2.5}>
            <Picture
              png={physicalVirtualPng as TImage<'png'>}
              webp={physicalVirtualWebp as TImage<'webp'>}
              alt={t('main-page.projects.arbipay.board.elements.3.title')}
              sx={{ maxWidth: { xs: '64px', md: '72px' } }}
            />

            <Stack>
              <Typography variant="text18Bold">
                {t('main-page.projects.arbipay.board.elements.3.title')}
              </Typography>
            </Stack>
          </Stack>
        </BoardElement>

        <BoardElement>
          <Stack direction="row" alignItems="center" gap={2.5}>
            <Picture
              png={posTerminalsPng as TImage<'png'>}
              webp={posTerminalsWebp as TImage<'webp'>}
              alt={t('main-page.projects.arbipay.board.elements.4.title')}
              sx={{ maxWidth: { xs: '64px', md: '72px' } }}
            />

            <Stack>
              <Typography variant="text18Bold">
                {t('main-page.projects.arbipay.board.elements.4.title')}
              </Typography>
            </Stack>
          </Stack>
        </BoardElement>

        <BoardElement>
          <Stack direction="row" alignItems="center" gap={2.5}>
            <Picture
              png={cashDepositAndWithdrawalPng as TImage<'png'>}
              webp={cashDepositAndWithdrawalWebp as TImage<'webp'>}
              alt={t('main-page.projects.arbipay.board.elements.4.title')}
              sx={{ maxWidth: { xs: '64px', md: '72px' } }}
            />

            <Stack>
              <Typography variant="text18Bold">
                {t('main-page.projects.arbipay.board.elements.5.title')}
              </Typography>
            </Stack>
          </Stack>
        </BoardElement>
      </Board>
    </Stack>
  )
}
